<template>
  <div class="user-management">
    <div class="page-header">
      <h2>客户端用户管理</h2>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="queryParams" class="search-form">
      <el-form-item label="关键词">
        <el-input
          v-model="queryParams.keyword"
          placeholder="手机号/昵称"
          clearable
          style="width: 200px"
        />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item label="注册时间">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="timestamp"
          style="width: 300px"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="handleQuery">搜索</el-button>
        <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="userList" border style="width: 100%">
      <el-table-column label="用户ID" prop="id" width="80" align="center" />
      <el-table-column label="头像" width="80" align="center">
        <template slot-scope="scope">
          <el-avatar v-if="scope.row.avatar" :src="scope.row.avatar" :size="40" />
          <el-avatar v-else icon="el-icon-user-solid" :size="40" />
        </template>
      </el-table-column>
      <el-table-column label="昵称" prop="user_name" min-width="120" />
      <el-table-column label="手机号" prop="mobile" width="120" align="center" />
      <el-table-column label="积分" prop="points" width="100" align="center">
        <template slot-scope="scope">
          <span style="color: #f56c6c; font-weight: bold;">{{ scope.row.points || 0 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="余额" prop="balance" width="120" align="center">
        <template slot-scope="scope">
          <span style="color: #409eff; font-weight: bold;">{{ Number(scope.row.balance || 0).toFixed(2) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="VIP状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_vip === 1" type="warning" size="small">
            {{ scope.row.vip_expire_time ? '会员' : '永久会员' }}
          </el-tag>
          <el-tag v-else type="info" size="small">非会员</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="VIP到期时间" prop="vip_expire_time" width="180" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.is_vip === 1">
            {{ scope.row.vip_expire_time || '永久' }}
          </span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="邀请人ID" prop="inviter_id" width="100" align="center" />
      <el-table-column label="状态" width="80" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
            {{ scope.row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="短信白名单" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.sms_code_white === 1 ? 'success' : 'info'" size="small">
            {{ scope.row.sms_code_white === 1 ? '已开启' : '已关闭' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="注册时间" prop="create_time" width="180" align="center">
        <template slot-scope="scope">
          {{ formatDate(scope.row.create_time) }}
        </template>
      </el-table-column>
      <el-table-column label="最后登录" prop="last_login_time" width="180" align="center">
        <template slot-scope="scope">
          {{ formatDate(scope.row.last_login_time) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right" align="center">
        <template slot-scope="scope">
          <div class="action-buttons">
            <el-button size="mini" type="text" class="detail-btn" @click="handleDetail(scope.row)">详情</el-button>
            <el-divider direction="vertical" />
            <el-dropdown trigger="click" @command="(command) => handleCommand(command, scope.row)">
              <span class="el-dropdown-link">
                更多<i class="el-icon-arrow-down el-icon--right" />
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-if="scope.row.status === 1"
                  command="disable"
                  class="disable-item"
                >禁用</el-dropdown-item>
                <el-dropdown-item
                  v-else
                  command="enable"
                  class="enable-item"
                >启用</el-dropdown-item>
                <el-dropdown-item command="resetPassword">修改密码</el-dropdown-item>
                <el-dropdown-item command="addPoints" class="points-item">增加积分</el-dropdown-item>
                <el-dropdown-item command="addBalance" class="balance-item">充值余额</el-dropdown-item>
                <el-dropdown-item command="withdrawalBalance" class="withdrawal-item">提现余额</el-dropdown-item>
                <el-dropdown-item command="rebindInviter">重新绑定</el-dropdown-item>
                <el-dropdown-item command="sendCoupon" class="coupon-item">发送优惠券</el-dropdown-item>
                <el-dropdown-item command="setVip" class="vip-item">VIP设置</el-dropdown-item>
                <el-dropdown-item
                  :command="scope.row.sms_code_white === 1 ? 'closeSmsWhite' : 'openSmsWhite'"
                  :class="scope.row.sms_code_white === 1 ? 'sms-white-close-item' : 'sms-white-item'"
                >
                  {{ scope.row.sms_code_white === 1 ? '关闭短信白名单' : '开启短信白名单' }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="queryParams.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="queryParams.limit"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 增加积分弹窗 -->
    <add-points-dialog ref="addPointsDialog" @refresh="getList" />

    <!-- 重新绑定邀请人弹窗 -->
    <rebind-inviter-dialog ref="rebindInviterDialog" @refresh="getList" />

    <!-- 发送优惠券弹窗 -->
    <send-coupon-dialog ref="sendCouponDialog" @refresh="getList" />

    <!-- 充值余额弹窗 -->
    <add-balance-dialog ref="addBalanceDialog" @refresh="getList" />

    <!-- 提现余额弹窗 -->
    <withdrawal-balance-dialog ref="withdrawalBalanceDialog" @refresh="getList" />

    <!-- VIP设置弹窗 -->
    <set-vip-dialog ref="setVipDialog" @refresh="getList" />
  </div>
</template>

<script>
import { getUserList, disableUser, enableUser, resetPassword, addPoints, rebindInviter, sendCoupon, addBalances, configSmsWhite, withdrawalBalances, setVip } from '@/api/user'
import AddPointsDialog from './components/AddPointsDialog.vue'
import RebindInviterDialog from './components/RebindInviterDialog.vue'
import SendCouponDialog from './components/SendCouponDialog.vue'
import AddBalanceDialog from './components/AddBalanceDialog.vue'
import WithdrawalBalanceDialog from './components/WithdrawalBalanceDialog.vue'
import SetVipDialog from './components/SetVipDialog.vue'

export default {
  name: 'ClientUserList',
  components: { AddPointsDialog, RebindInviterDialog, SendCouponDialog, AddBalanceDialog, WithdrawalBalanceDialog, SetVipDialog },
  data() {
    return {
      loading: false,
      userList: [],
      total: 0,
      queryParams: {
        page: 1,
        limit: 20,
        keyword: '',
        status: '',
        start_time: '',
        end_time: ''
      },
      dateRange: []
    }
  },
  created() {
    this.getList()
  },
  methods: {
    // 获取用户列表
    async getList() {
      this.loading = true
      try {
        if (this.dateRange && this.dateRange.length === 2) {
          this.queryParams.start_time = this.dateRange[0]
          this.queryParams.end_time = this.dateRange[1]
        } else {
          this.queryParams.start_time = ''
          this.queryParams.end_time = ''
        }

        const res = await getUserList(this.queryParams)
        if (res.code === 200) {
          this.userList = res.data.list || []
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.msg || '获取用户列表失败')
        }
      } catch (error) {
        this.$message.error(error.message || '获取用户列表失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleQuery() {
      this.queryParams.page = 1
      this.getList()
    },

    // 重置
    resetQuery() {
      this.queryParams = {
        page: 1,
        limit: 20,
        keyword: '',
        status: '',
        start_time: '',
        end_time: ''
      }
      this.dateRange = []
      this.getList()
    },

    // 查看详情
    handleDetail(row) {
      this.$router.push({
        path: '/dashboard/user-management/client-user/detail',
        query: { id: row.id }
      })
    },

    // 禁用用户
    async handleDisable(row) {
      this.$prompt('请输入禁用原因', '禁用用户', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /\S/,
        inputErrorMessage: '请输入禁用原因'
      }).then(async ({ value }) => {
        try {
          const res = await disableUser({ user_id: row.id, reason: value })
          if (res.code === 200) {
            this.$message.success('禁用成功')
            this.getList()
          } else {
            this.$message.error(res.msg || '禁用失败')
          }
        } catch (error) {
          this.$message.error(error.message || '禁用失败')
        }
      }).catch(() => {})
    },

    // 启用用户
    async handleEnable(row) {
      this.$confirm('确定要启用该用户吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await enableUser({ user_id: row.id })
          if (res.code === 200) {
            this.$message.success('启用成功')
            this.getList()
          } else {
            this.$message.error(res.msg || '启用失败')
          }
        } catch (error) {
          this.$message.error(error.message || '启用失败')
        }
      }).catch(() => {})
    },

    // 修改密码
    async handleResetPassword(row) {
      const { value: password } = await this.$prompt('请输入新密码', '修改密码', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{6,20}$/,
        inputErrorMessage: '密码需6-20位，包含字母和数字'
      }).catch(() => {
        return
      })

      try {
        const res = await resetPassword({ user_id: row.id, new_password: password })
        if (res.code === 200) {
          this.$message.success('修改成功')
        } else {
          this.$message.error(res.msg || '修改失败')
        }
      } catch (error) {
        this.$message.error(error.message || '修改失败')
      }
    },

    // 增加积分
    handleAddPoints(row) {
      this.$refs.addPointsDialog.open(row)
    },

    // 重新绑定邀请人
    handleRebindInviter(row) {
      this.$refs.rebindInviterDialog.open(row)
    },

    // 发送优惠券
    handleSendCoupon(row) {
      this.$refs.sendCouponDialog.open(row)
    },

    // 充值余额
    handleAddBalance(row) {
      this.$refs.addBalanceDialog.open(row)
    },

    // 提现余额
    handleWithdrawalBalance(row) {
      this.$refs.withdrawalBalanceDialog.open(row)
    },

    // 开启短信白名单
    handleOpenSmsWhite(row) {
      this.$confirm(
        `确定要将用户 ${row.user_name || row.mobile} 开启短信白名单吗？`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const res = await configSmsWhite({ user_id: row.id, sms_code_white: 1 })
          if (res.code === 200) {
            this.$message.success('开启成功')
            this.getList()
          } else {
            this.$message.error(res.msg || '开启失败')
          }
        } catch (error) {
          this.$message.error(error.message || '开启失败')
        }
      }).catch(() => {})
    },

    // 关闭短信白名单
    handleCloseSmsWhite(row) {
      this.$confirm(
        `确定要将用户 ${row.user_name || row.mobile} 关闭短信白名单吗？`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const res = await configSmsWhite({ user_id: row.id, sms_code_white: 0 })
          if (res.code === 200) {
            this.$message.success('关闭成功')
            this.getList()
          } else {
            this.$message.error(res.msg || '关闭失败')
          }
        } catch (error) {
          this.$message.error(error.message || '关闭失败')
        }
      }).catch(() => {})
    },

    // 下拉菜单操作
    handleCommand(command, row) {
      switch (command) {
        case 'disable':
          this.handleDisable(row)
          break
        case 'enable':
          this.handleEnable(row)
          break
        case 'resetPassword':
          this.handleResetPassword(row)
          break
        case 'addPoints':
          this.handleAddPoints(row)
          break
        case 'addBalance':
          this.handleAddBalance(row)
          break
        case 'withdrawalBalance':
          this.handleWithdrawalBalance(row)
          break
        case 'rebindInviter':
          this.handleRebindInviter(row)
          break
        case 'sendCoupon':
          this.handleSendCoupon(row)
          break
        case 'setVip':
          this.handleSetVip(row)
          break
        case 'openSmsWhite':
          this.handleOpenSmsWhite(row)
          break
        case 'closeSmsWhite':
          this.handleCloseSmsWhite(row)
          break
      }
    },

    // VIP设置
    handleSetVip(row) {
      this.$refs.setVipDialog.open(row)
    },

    // 分页
    handleSizeChange(val) {
      this.queryParams.limit = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.queryParams.page = val
      this.getList()
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-'
      // 如果是时间戳格式（数字），转换为秒级时间戳
      if (typeof dateStr === 'number') {
        const date = new Date(dateStr * 1000)
        return date.toLocaleString('zh-CN')
      }
      // 如果是字符串格式（如 "2026-01-01 21:09:33"），直接返回
      return dateStr
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.detail-btn {
  color: #409eff;
  font-size: 12px;
}

.detail-btn:hover {
  color: #66b1ff;
}

.el-dropdown-link {
  cursor: pointer;
  color: #409eff;
  font-size: 12px;
}

.el-dropdown-link:hover {
  color: #66b1ff;
}

/* 下拉菜单项样式 */
.el-dropdown-menu__item.disable-item {
  color: #e6a23c;
}

.el-dropdown-menu__item.disable-item:hover {
  background-color: #fdf6ec;
  color: #ebb563;
}

.el-dropdown-menu__item.enable-item {
  color: #67c23a;
}

.el-dropdown-menu__item.enable-item:hover {
  background-color: #f0f9ff;
  color: #85ce61;
}

.el-dropdown-menu__item.points-item {
  color: #f56c6c;
}

.el-dropdown-menu__item.points-item:hover {
  background-color: #fef0f0;
  color: #f78989;
}

.el-dropdown-menu__item.balance-item {
  color: #409eff;
}

.el-dropdown-menu__item.balance-item:hover {
  background-color: #ecf5ff;
  color: #66b1ff;
}

.el-dropdown-menu__item.withdrawal-item {
  color: #e6a23c;
}

.el-dropdown-menu__item.withdrawal-item:hover {
  background-color: #fdf6ec;
  color: #ebb563;
}

.el-dropdown-menu__item.coupon-item {
  color: #909399;
}

.el-dropdown-menu__item.coupon-item:hover {
  background-color: #f4f4f5;
  color: #a6a9ad;
}

.el-dropdown-menu__item.sms-white-item {
  color: #67c23a;
}

.el-dropdown-menu__item.sms-white-item:hover {
  background-color: #f0f9ff;
  color: #85ce61;
}

.el-dropdown-menu__item.sms-white-close-item {
  color: #909399;
}

.el-dropdown-menu__item.sms-white-close-item:hover {
  background-color: #f4f4f5;
  color: #a6a9ad;
}

.el-dropdown-menu__item.vip-item {
  color: #e6a23c;
}

.el-dropdown-menu__item.vip-item:hover {
  background-color: #fdf6ec;
  color: #ebb563;
}
</style>
